import { type Dispatch, type MouseEvent, type SetStateAction } from "react";
import { useSearchParams } from "react-router-dom";
import type { ModalAction, ModalStateFirst } from "@features/modal-info";

export type { ModalAction, ModalStateFirst };

type UseActionType = {
  handleSend: any;
  handleSendMany: any;
  handleRecall: any;
  handleReject: any;
  handleUpdate: any;
  deleteSectionEntry: any;
  setModalState: Dispatch<SetStateAction<ModalStateFirst>>;
  setIsForm: Dispatch<SetStateAction<boolean>>;
  setFormKey: Dispatch<SetStateAction<number>>;
  setTitleInit: Dispatch<SetStateAction<string>>;
  setInitialValues: Dispatch<SetStateAction<any>>;
  setInitUserInfo: Dispatch<SetStateAction<{
    name: string;
    organization: string;
    position: string;
  } | null>>;
  setSelectedRowKeys: any;
  setSearchParams: any;
  mainJournalData: any;
  mapSectionItemToFormPayload: any;
};

export const useAction = ({
  handleSend,
  handleSendMany,
  handleRecall,
  handleReject,
  handleUpdate,
  deleteSectionEntry,
  setModalState,
  setIsForm,
  setFormKey,
  setTitleInit,
  setInitialValues,
  setInitUserInfo,
  setSelectedRowKeys,
  setSearchParams,
  mainJournalData,
  mapSectionItemToFormPayload,
}: UseActionType) => {
  const [searchParams] = useSearchParams();

  const itemNoVersionId = searchParams.get("itemNoVersionId");
  const action = searchParams.get("action");

  const handleAction = async (actionType: ModalAction) => {
    switch (actionType.type) {
      case "SEND":
        await handleSend(actionType.payload);
        break;

      case "REJECT":
        await handleReject({
          sectionOneListId: actionType.payload.sectionOneListId,
          itemNoVersionId: actionType.payload.itemNoVersionId,
          signerEmployeeId: actionType.payload.signerEmployeeId,
          comment: actionType.payload.comment,
        });
        break;

      case "DELETE":
        await deleteSectionEntry({
          params: { path: { itemNoVersionId: actionType.payload.itemNoVersionId } },
        });
        break;

      case "RECALL":
        handleRecall(actionType.payload);
        break;

      case "CLEAR_SELECTION":
        setSelectedRowKeys([]);
        break;

      case "SEND_MULTIPLE":
        handleSendMany({ itemsIds: actionType.payload });
        break;

      case "CANCEL_SIGN":
        await handleReject({
          sectionOneListId: actionType.payload.sectionOneListId,
          itemNoVersionId: actionType.payload.itemNoVersionId,
          signerEmployeeId: actionType.payload.signerEmployeeId,
          comment: actionType.payload.comment,
        });
        break;

      case "UPDATE":
        await handleUpdate(actionType.payload);
        break;

      default:
        break;
    }
  };

  const handleCloseForm = (
    status: boolean,
    event?: MouseEvent<HTMLSpanElement>,
    title?: string,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    event && event.stopPropagation();
    setIsForm(status);
    setFormKey(Date.now());
    setTitleInit(title || "Добавление записи в раздел 1 ОЖР");
    setInitUserInfo(null);
    setInitialValues(null);
    setModalState({
      visible: false,
      title: "",
      info: "",
      confirmText: "",
      cancelText: "",
      inputPlaceholder: "",
      inputValue: "",
      isInputModal: false,
      onConfirm: null,
      action: null,
      isDivider: false,
      confirmDisabled: false,
    });
    setSearchParams({});
  };

  const handleOpenForm = (
    status: boolean,
    event?: MouseEvent<HTMLSpanElement>,
    title?: string,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    event && event.stopPropagation();
    setIsForm(status);
    setTitleInit(title || "Добавление записи в раздел 1 ОЖР");
    setFormKey(Date.now());
    setInitUserInfo(null);
    setInitialValues(null);
    setSearchParams({});
  };

  const handleEdit = (item: any) => {
    setInitUserInfo({
      name: `${item?.userCompany?.user?.firstName} ${item?.userCompany?.user?.middleName} ${item?.userCompany?.user?.surname}`,
      organization: item?.company?.fullName || "",
      position: item?.userCompany?.user?.position || "",
    });
    setInitialValues(mapSectionItemToFormPayload(item, mainJournalData));
    setTitleInit("Редактирование записи");
    setIsForm(true);
    setSearchParams({ itemNoVersionId: item?.itemNoVersionId, action: "EDIT" });
  };

  const handleView = (item: any) => {
    setSearchParams({ itemNoVersionId: item?.itemNoVersionId, action: "VIEW" });
  };

  return { 
    handleAction, 
    handleCloseForm, 
    handleOpenForm, 
    handleEdit, 
    handleView,
    itemNoVersionId,
    action,
  };
}; 
